import React from 'react';
import { Icon } from '@iconify-icon/react';
import { Button } from '@mantine/core';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

import PrivacyFlowDiagram from './PrivacyFlowDiagram';

const PrivacyHero: React.FC = () => (
  <section className="relative overflow-hidden">
    {/* Background: subtle grid + gradient orbs (matches landing hero) */}
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0047900a_1px,transparent_1px),linear-gradient(to_bottom,#0047900a_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" />
      <div className="absolute -right-32 -top-32 h-[500px] w-[500px] rounded-full bg-blue-100/30 blur-[100px]" />
      <div className="absolute -bottom-20 -left-20 h-[300px] w-[300px] rounded-full bg-amber-50/60 blur-[80px]" />
    </div>

    <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-6 md:pb-20 md:pt-16 lg:px-8 lg:pb-24 lg:pt-20">
      <div className="flex flex-col gap-12 lg:flex-row lg:items-center lg:gap-16">
        {/* ─── Left column ─── */}
        <div className="flex-1 lg:max-w-[55%]">
          <div className="hero-badge-entrance mb-8 inline-flex items-center gap-2.5 rounded-full border border-blue-100 bg-gradient-to-r from-blue-50/80 to-white px-4 py-2 shadow-sm backdrop-blur-sm">
            <Icon icon="tabler:shield-lock" className="text-base text-[#004790]" />
            <span className="text-sm font-semibold text-gray-800">
              Privacy is the default, not an upgrade
            </span>
          </div>

          <h1 className="hero-headline-entrance text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-[3.5rem] lg:leading-[1.1] xl:text-6xl">
            Private AI,
            <br />
            <span className="hero-gradient-text">built for business.</span>
          </h1>

          <p className="hero-desc-entrance mt-6 max-w-xl text-lg leading-relaxed text-gray-500">
            When Chat.co uses AWS Bedrock, inference has{' '}
            <span className="font-semibold text-gray-900">zero data retention by default</span> and
            inputs and outputs are not shared with the underlying model providers for training.
          </p>
          <p className="hero-desc-entrance mt-4 max-w-xl text-base leading-relaxed text-gray-500">
            Protect company knowledge with authenticated agents, role-based access, encryption, and
            grounded answers that show their sources.
          </p>

          <div className="hero-cta-entrance mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              size="lg"
              component={Link}
              href={PageLink.login}
              className="hero-cta-btn group relative overflow-hidden rounded-xl bg-primary-600 px-8 text-base font-semibold shadow-lg shadow-blue-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30">
              Start 14-Day Trial
              <svg
                className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
            <Link
              href="/privacy-policy"
              className="text-sm font-medium text-gray-500 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-800">
              Prefer the legal version? Read our Privacy Policy
            </Link>
          </div>

          {/* Reassurance row */}
          <div className="hero-stats-entrance mt-12 grid grid-cols-1 gap-4 border-t border-gray-100 pt-8 sm:grid-cols-3">
            {[
              { icon: 'tabler:database-off', text: 'Zero-retention Bedrock inference' },
              { icon: 'tabler:brain-off', text: 'No training on inputs or outputs' },
              { icon: 'tabler:lock-square-rounded', text: 'Encrypted in transit & at rest' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2.5">
                <Icon icon={item.icon} className="shrink-0 text-xl text-[#004790]" />
                <span className="text-sm font-medium text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Right column: flow diagram ─── */}
        <div className="hero-demo-entrance w-full lg:w-[45%]">
          <PrivacyFlowDiagram />
        </div>
      </div>
    </div>
  </section>
);

export default PrivacyHero;
